import { useState } from "react";
import { Trophy, TrendingUp, ArrowRight, Brain, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { SimulationState, SimStock } from "@/hooks/useSimulator";
import BehavioralReport from "./BehavioralReport";
import ModuleFeedbackCard from "@/components/ai/ModuleFeedbackCard";

interface Props {
  simulation: SimulationState;
  stocks: SimStock[];
  onTransfer: () => Promise<{ success: boolean; finalValue: number; startingCapital: number; totalReturn: number; behavioral_metrics?: unknown } | null>;
  onBack: () => void;
}

const CompletionScreen = ({ simulation: sim, stocks, onTransfer, onBack }: Props) => {
  const [transferring, setTransferring] = useState(false);
  const [transferred, setTransferred] = useState(false);
  const [result, setResult] = useState<{ finalValue: number; totalReturn: number } | null>(null);
  const [showReport, setShowReport] = useState(false);

  const portfolioValue = Object.entries(sim.holdings).reduce(
    (sum, [ticker, h]) => sum + (sim.stock_prices[ticker] || 0) * h.shares,
    0
  );
  const netWorth = Number(sim.cash_balance) + portfolioValue;
  const totalReturn = ((netWorth - Number(sim.starting_capital)) / Number(sim.starting_capital)) * 100;

  let bestTicker = "";
  let bestReturn = -Infinity;
  for (const [ticker, h] of Object.entries(sim.holdings)) {
    const price = sim.stock_prices[ticker] || 0;
    const ret = ((price - h.avgCost) / h.avgCost) * 100;
    if (ret > bestReturn) { bestReturn = ret; bestTicker = ticker; }
  }

  const hw500Start = sim.hw500_history[0]?.level || 1;
  const hw500End = sim.hw500_history[sim.hw500_history.length - 1]?.level || hw500Start;
  const hw500Return = ((hw500End - hw500Start) / hw500Start) * 100;

  const handleTransfer = async () => {
    setTransferring(true);
    const res = await onTransfer();
    if (res?.success) {
      setResult({ finalValue: res.finalValue, totalReturn: res.totalReturn });
      setTransferred(true);
    }
    setTransferring(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="glass-card glow-border max-w-md w-full p-8 space-y-6 max-h-[90vh] overflow-y-auto">
        {!showReport ? (
          <>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto">
                <Trophy className="w-8 h-8 text-accent" />
              </div>
              <h2 className="font-heading font-bold text-2xl text-foreground mt-4 mb-1">Simulation Complete!</h2>
              <p className="text-muted-foreground text-sm">Investing Fundamentals: Module · {sim.max_rounds} Rounds</p>
            </div>

            {/* Results */}
            <div className="space-y-3">
              <div className="p-4 rounded-xl border border-border bg-muted/30 text-center">
                <p className="text-muted-foreground text-xs">Starting Capital</p>
                <p className="text-foreground/70 font-heading font-bold text-lg">${Number(sim.starting_capital).toLocaleString()}</p>
              </div>
              <div className="p-4 rounded-xl border border-accent/30 bg-accent/5 text-center">
                <p className="text-accent/60 text-xs">Final Net Worth</p>
                <p className="text-accent font-heading font-bold text-2xl">${(result?.finalValue || netWorth).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                <p className={`text-sm font-heading ${totalReturn >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {totalReturn >= 0 ? "+" : ""}{(result?.totalReturn || totalReturn).toFixed(2)}% total return
                </p>
              </div>
            </div>

            {/* Comparison */}
            <div className="flex justify-center gap-6 text-xs">
              <div className="text-center">
                <p className="text-muted-foreground">Your Return</p>
                <p className={`font-heading font-bold ${totalReturn >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {totalReturn >= 0 ? "+" : ""}{totalReturn.toFixed(2)}%
                </p>
              </div>
              <div className="text-center">
                <p className="text-muted-foreground">HW500 Return</p>
                <p className={`font-heading font-bold ${hw500Return >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {hw500Return >= 0 ? "+" : ""}{hw500Return.toFixed(2)}%
                </p>
              </div>
              {bestTicker && (
                <div className="text-center">
                  <p className="text-muted-foreground">Best Pick</p>
                  <p className="font-heading font-bold text-accent">{bestTicker}</p>
                </div>
              )}
            </div>

            {/* AI Strategy Debrief */}
            <ModuleFeedbackCard
              moduleName="Investing Simulator"
              moduleKey="market_simulator"
              finalMetrics={{
                starting_capital: Number(sim.starting_capital),
                final_value: netWorth,
                total_return: totalReturn,
                rounds_played: sim.max_rounds,
                best_pick: bestTicker,
                best_return: bestReturn,
                hw500_return: hw500Return,
              }}
              outcome={totalReturn >= 0 ? "profit" : "loss"}
            />

            {/* View Psychology Report button */}
            <button
              onClick={() => setShowReport(true)}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-secondary/30 bg-secondary/5 text-secondary hover:bg-secondary/10 transition-all font-heading font-semibold text-sm"
            >
              <Brain className="w-4 h-4" />
              View Investor Psychology Report
            </button>

            {/* Transfer or Done */}
            {!transferred ? (
              <Button
                onClick={handleTransfer}
                disabled={transferring}
                className="w-full bg-accent/20 border border-accent/50 text-accent hover:bg-accent/30 font-heading font-bold text-base shadow-[0_0_25px_hsl(var(--accent)/0.3)]"
              >
                {transferring ? "Transferring..." : "Transfer to HeirWise Investing Account →"}
              </Button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 justify-center text-emerald-400 text-sm">
                  <TrendingUp className="w-4 h-4" />
                  <span className="font-heading font-bold">Portfolio transferred successfully!</span>
                </div>
                <p className="text-muted-foreground text-xs text-center">
                  Your final portfolio value of ${(result?.finalValue || netWorth).toLocaleString(undefined, { minimumFractionDigits: 2 })} has been added to your HeirWise Investment Account.
                </p>
                <Button
                  onClick={onBack}
                  className="w-full bg-muted/30 border border-border text-foreground/70 hover:bg-muted/50 font-heading"
                >
                  View Financial Dashboard <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
                <button
                  onClick={() => window.location.reload()}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted/30 font-heading text-sm transition-all"
                >
                  <RefreshCcw className="w-3.5 h-3.5" />
                  Play Again
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center mx-auto">
                <Brain className="w-6 h-6 text-secondary" />
              </div>
              <h2 className="font-heading font-bold text-xl text-foreground mt-3 mb-1">Investor Psychology Report</h2>
              <p className="text-muted-foreground text-xs">How your behavior affected your results</p>
            </div>

            <BehavioralReport metrics={sim.behavioral_metrics} maxRounds={sim.max_rounds} />

            <button
              onClick={() => setShowReport(false)}
              className="w-full py-2.5 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:bg-muted/30 font-heading text-sm transition-all"
            >
              ← Back to Results
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CompletionScreen;
